import React, { useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ClockIcon, BookMarkedIcon, UsersIcon, SunriseIcon } from 'lucide-react';
export function WhyNowSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section ref={ref} className="w-full bg-[#111111] py-28 px-6" data-id="element-209">
      <div className="max-w-5xl mx-auto" data-id="element-210">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-211">
          <p className="text-[#CF9B2E] uppercase tracking-[0.3em] text-sm font-bold mb-4 font-['Lato']" data-id="element-212">
            El momento es ahora
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#F5F0E8] mb-6 leading-tight" data-id="element-213">
            Por qué este libro
            <br data-id="element-214" />
            <span className="text-[#CF9B2E]" data-id="element-215">importa hoy más que nunca</span>
          </h2>
          <div className="gold-divider" data-id="element-216" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10" data-id="element-217">
          {/* Reason 1 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.1
        }} className="flex gap-6" data-id="element-218">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#C0392B]/20 flex items-center justify-center" data-id="element-219">
              <ClockIcon className="w-6 h-6 text-[#C0392B]" data-id="element-220" />
            </div>
            <div data-id="element-221">
              <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-222">
                La memoria no puede esperar
              </h3>
              <p className="text-[#9A9080] leading-relaxed" data-id="element-223">
                Los regímenes cuentan con el olvido. Cada año que pasa sin
                documentar lo ocurrido es una victoria para quienes destruyeron
                Venezuela. Este libro es un acto de resistencia.
              </p>
            </div>
          </motion.div>

          {/* Reason 2 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="flex gap-6" data-id="element-224">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#CF9B2E]/20 flex items-center justify-center" data-id="element-225">
              <BookMarkedIcon className="w-6 h-6 text-[#CF9B2E]" data-id="element-226" />
            </div>
            <div data-id="element-227">
              <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-228">
                Dejar registro para la historia
              </h3>
              <p className="text-[#9A9080] leading-relaxed" data-id="element-229">
                Las generaciones futuras necesitan saber qué pasó realmente. No
                la versión oficial, no la narrativa conveniente — sino la verdad
                cruda y documentada de lo que vivió el pueblo venezolano.
              </p>
            </div>
          </motion.div>

          {/* Reason 3 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.3
        }} className="flex gap-6" data-id="element-230">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#1A5276]/30 flex items-center justify-center" data-id="element-231">
              <UsersIcon className="w-6 h-6 text-[#4A90C4]" data-id="element-232" />
            </div>
            <div data-id="element-233">
              <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-234">
                Un mensaje a las nuevas generaciones
              </h3>
              <p className="text-[#9A9080] leading-relaxed" data-id="element-235">
                Los hijos de la diáspora crecen sin entender completamente por
                qué sus padres lloraban al hablar de su país. Este libro les da
                respuestas. Les devuelve su historia.
              </p>
            </div>
          </motion.div>

          {/* Reason 4 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.4
        }} className="flex gap-6" data-id="element-236">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#CF9B2E]/20 flex items-center justify-center" data-id="element-237">
              <SunriseIcon className="w-6 h-6 text-[#CF9B2E]" data-id="element-238" />
            </div>
            <div data-id="element-239">
              <h3 className="text-xl font-bold text-[#F5F0E8] mb-3" data-id="element-240">
                El final de un ciclo político
              </h3>
              <p className="text-[#9A9080] leading-relaxed" data-id="element-241">
                Estamos ante un punto de inflexión histórico. Entender cómo
                llegamos aquí es el primer paso para construir lo que viene.
                Venezuela tiene futuro — pero ese futuro necesita conocer su
                pasado.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}